<template>
  <div class="auth-page">

    <!-- Left branding panel -->
    <div class="auth-brand">
      <div class="auth-brand__inner">
        <router-link to="/" class="auth-logo">
          <img v-if="company && company.logo" :src="imgUrl + 'logo/' + company.logo" alt="Structurecity" />
          <span v-else class="auth-logo__text">Structure<span>city</span></span>
        </router-link>
        <div class="auth-brand__body">
          <h1>Reset your password</h1>
          <p>Forgot your password? No worries — we'll send you a reset link so you can get back in quickly.</p>
          <div class="auth-steps">
            <div class="auth-step">
              <span class="auth-step__num">1</span>
              <span>Enter your registered email address</span>
            </div>
            <div class="auth-step">
              <span class="auth-step__num">2</span>
              <span>Check your inbox for the reset link</span>
            </div>
            <div class="auth-step">
              <span class="auth-step__num">3</span>
              <span>Set a new password and sign in</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right form panel -->
    <div class="auth-panel">
      <div class="auth-panel__inner">

        <!-- Mobile logo -->
        <router-link to="/" class="auth-logo auth-logo--mobile">
          <img v-if="company && company.logo" :src="imgUrl + 'logo/' + company.logo" alt="Structurecity" />
          <span v-else class="auth-logo__text">Structure<span>city</span></span>
        </router-link>

        <div class="auth-panel__header">
          <div class="auth-panel__icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
          </div>
          <h2>Forgot password?</h2>
          <p>Enter your email and we'll send you a link to reset your password.</p>
        </div>

        <div v-if="errorMsg" class="sc-alert sc-alert--error">{{ errorMsg }}</div>
        <div v-if="successMsg" class="sc-alert sc-alert--success">{{ successMsg }}</div>

        <form v-if="!successMsg" @submit.prevent="submit">
          <div class="sc-field">
            <label>Email address</label>
            <input type="email" v-model="email" placeholder="you@example.com" required autocomplete="email" />
          </div>
          <button type="submit" class="auth-btn" :disabled="loading">
            <span v-if="loading" class="sc-spinner"></span>
            <span v-else>Send reset link</span>
          </button>
        </form>

        <div v-if="successMsg" class="auth-success-actions">
          <router-link to="/sign-in" class="auth-btn">Back to sign in</router-link>
        </div>

        <div class="auth-bottom-links">
          <router-link to="/sign-in" class="auth-link">← Back to sign in</router-link>
          <router-link to="/register" class="auth-link">Create an account</router-link>
        </div>

        <p class="auth-back"><router-link to="/" class="auth-link">← Back to home</router-link></p>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ForgotPassword',

  data() {
    return {
      imgUrl: process.env.VUE_APP_IMAGE_URL || '',
      company: null,
      email: '',
      loading: false,
      errorMsg: '',
      successMsg: '',
    };
  },

  watch: {
    $route: { immediate: true, handler(to) { document.title = to.meta && to.meta.title ? to.meta.title : 'Forgot Password — Structurecity'; } },
  },

  created() {
    axios(process.env.VUE_APP_API_URL + 'company')
      .then(res => { if (res && res.data) this.company = res.data; })
      .catch(() => {});
  },

  methods: {
    submit() {
      this.loading = true;
      this.errorMsg = '';
      const fd = new FormData();
      fd.append('email', this.email);
      axios.post(process.env.VUE_APP_API_URL + 'auth/forgot-password', fd)
        .then(res => {
          if (res.status === 200) {
            this.successMsg = 'Reset link sent! Please check your email inbox (and spam folder) for the link.';
          } else {
            this.errorMsg = res.message || 'Could not send reset email. Please try again.';
          }
        })
        .catch(() => { this.errorMsg = 'Could not send reset email. Please try again.'; })
        .finally(() => { this.loading = false; });
    },
  },
};
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; }

.auth-page {
  display: grid; grid-template-columns: 1fr 1fr;
  min-height: 100vh;
}

/* ── Left branding panel ── */
.auth-brand {
  background: linear-gradient(145deg, #0a1628 0%, #1e3a5f 50%, #1a4a7a 100%);
  padding: 3rem; display: flex; align-items: center; justify-content: center;
  position: relative; overflow: hidden;
}
.auth-brand::before {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background-image: radial-gradient(circle, rgba(255,255,255,.05) 1px, transparent 1px);
  background-size: 36px 36px;
}
.auth-brand__inner {
  position: relative; z-index: 1; max-width: 420px; width: 100%;
  display: flex; flex-direction: column; gap: 2.5rem;
}

.auth-logo { display: flex; align-items: center; text-decoration: none; }
.auth-logo img { height: 42px; object-fit: contain; }
.auth-logo__text { font-size: 1.75rem; font-weight: 800; color: #fff; letter-spacing: -.03em; }
.auth-logo__text span { color: #f59e0b; }
.auth-logo--mobile { display: none; }

.auth-brand__body { display: flex; flex-direction: column; gap: 1.25rem; }
.auth-brand__body h1 {
  font-size: clamp(1.5rem, 2.5vw, 2rem); font-weight: 800; color: #fff;
  line-height: 1.2; letter-spacing: -.03em; margin: 0;
}
.auth-brand__body p { color: rgba(255,255,255,.65); font-size: 1rem; line-height: 1.65; margin: 0; }

.auth-steps { display: flex; flex-direction: column; gap: 1rem; margin-top: .5rem; }
.auth-step {
  display: flex; align-items: center; gap: 1rem;
  color: rgba(255,255,255,.85); font-size: .9375rem; font-weight: 500;
}
.auth-step__num {
  width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
  background: rgba(245,158,11,.2); border: 1px solid rgba(245,158,11,.4);
  color: #f59e0b; font-weight: 700; font-size: .875rem;
  display: flex; align-items: center; justify-content: center;
}

/* ── Right form panel ── */
.auth-panel {
  background: #fff; display: flex; align-items: center; justify-content: center;
  padding: 3rem 2rem;
}
.auth-panel__inner {
  width: 100%; max-width: 400px; display: flex; flex-direction: column; gap: 1.5rem;
}

.auth-panel__icon {
  width: 56px; height: 56px; border-radius: 14px;
  background: rgba(30,58,95,.08); color: #1e3a5f;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: .25rem;
}
.auth-panel__header { display: flex; flex-direction: column; gap: .5rem; }
.auth-panel__header h2 {
  font-size: 1.875rem; font-weight: 800; color: #0f172a;
  margin: 0; letter-spacing: -.03em;
}
.auth-panel__header p { color: #64748b; font-size: 1rem; margin: 0; }

/* ── Fields ── */
.sc-field { display: flex; flex-direction: column; gap: .375rem; margin-bottom: 1rem; }
.sc-field label { font-size: .875rem; font-weight: 600; color: #334155; }
.sc-field input {
  width: 100%; padding: .6875rem 1rem;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  font-family: inherit; font-size: .9375rem; color: #0f172a;
  background: #fff; outline: none; transition: border-color .15s, box-shadow .15s;
}
.sc-field input:focus { border-color: #1e3a5f; box-shadow: 0 0 0 3px rgba(30,58,95,.1); }

.auth-btn {
  width: 100%; padding: .8125rem 1.5rem;
  background: #1e3a5f; color: #fff; border: none; border-radius: 10px;
  font-family: inherit; font-size: 1rem; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: .5rem;
  text-decoration: none;
  transition: background .15s, transform .1s, box-shadow .15s;
}
.auth-btn:hover:not(:disabled) { background: #2a4f7c; box-shadow: 0 4px 16px rgba(30,58,95,.3); color: #fff; text-decoration: none; }
.auth-btn:active:not(:disabled) { transform: translateY(1px); }
.auth-btn:disabled { opacity: .6; cursor: not-allowed; }

.auth-success-actions { display: flex; flex-direction: column; gap: .75rem; }

.auth-bottom-links { display: flex; justify-content: space-between; gap: .5rem; }
.auth-link { color: #1e3a5f; text-decoration: none; font-size: .875rem; font-weight: 500; }
.auth-link:hover { text-decoration: underline; }

.auth-back { text-align: center; margin: 0; }
.auth-back .auth-link { color: #94a3b8; }

/* ── Alerts ── */
.sc-alert { padding: .75rem 1rem; border-radius: 8px; font-size: .875rem; }
.sc-alert--error   { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
.sc-alert--success { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }

/* ── Spinner ── */
.sc-spinner {
  display: inline-block; width: 18px; height: 18px;
  border: 2px solid rgba(255,255,255,.3); border-top-color: #fff;
  border-radius: 50%; animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Responsive ── */
@media (max-width: 900px) {
  .auth-page { grid-template-columns: 1fr; }
  .auth-brand { display: none; }
  .auth-logo--mobile { display: flex; }
  .auth-panel { padding: 2.5rem 1.5rem; min-height: 100vh; }
}
</style>
